/**
 * What a build knows about itself: its steps, its log, and when it went wrong,
 * why. Screen spec 5.5 and 5.6.
 *
 * It lives in its own module rather than inline in seed.ts for one reason:
 * the build failure is the prototype's hero state, and three of its strings
 * are specified verbatim (the machine error, the raw build log, the fix
 * request payload). A verbatim string buried inside a scenario function is a
 * string somebody eventually "improves". Here they are the file's subject,
 * with the specification section written next to each one.
 *
 * seed.ts composes these onto a deploy. Nothing here reads the store, so the
 * import goes one way and the two files never race.
 */

import type { BuildDiagnosis, RecordedStep } from '../types';

/**
 * The five steps a build of an app that already exists runs.
 *
 * These are the five the raw build log below marks with its own step rule, so
 * the timeline row, the deploy detail ledger and the log a person opens
 * underneath it all name the same five things. Two vocabularies for one
 * artifact is how a person stops trusting the summary.
 */
const STEP_SHARES: Array<[key: string, label: string, share: number]> = [
  ['deps.install', 'Install dependencies', 0.22],
  ['config.read', 'Read your app configuration', 0.02],
  ['build', 'Build your app', 0.52],
  ['upload', 'Upload', 0.15],
  ['publish', 'Publish', 0.09],
];

/**
 * A finished build's recorded steps, apportioned out of its total duration.
 * The share table is fixed, so a 41 second deploy and a 58 second deploy have
 * the same shape, and a person who reads two of them learns the shape once.
 * The last step takes the remainder, so the parts always sum to the whole a
 * person was already told.
 */
export function builtSteps(durationMs: number): RecordedStep[] {
  // Apportioned in whole seconds rather than in milliseconds, because seconds
  // are what the ledger prints. Rounding five millisecond shares up to the
  // second gives five numbers that do not add up to the total on the row above
  // them, and a person who adds up a ledger and gets the wrong answer stops
  // believing the ledger.
  const total = Math.round(durationMs / 1000);
  let left = total;
  return STEP_SHARES.map(([key, label, share], index) => {
    const last = index === STEP_SHARES.length - 1;
    const seconds = last ? left : Math.max(1, Math.round(total * share));
    left -= seconds;
    return { key, label, ms: seconds * 1000, status: 'succeeded' };
  });
}

/**
 * The failed build's steps, timed off the raw log rather than off the share
 * table: install finishes in 8.1s, the configuration read in 0.2s, and the
 * build stops after 15.9s. Upload and Publish never started, which is a skip,
 * not a failure, and the ledger draws them struck through rather than red.
 * Two of five steps did work, and the screen says so.
 */
export const FAILED_STEPS: RecordedStep[] = [
  { key: 'deps.install', label: 'Install dependencies', ms: 8_100, status: 'succeeded' },
  { key: 'config.read', label: 'Read your app configuration', ms: 200, status: 'succeeded' },
  { key: 'build', label: 'Build your app', ms: 15_900, status: 'failed' },
  { key: 'upload', label: 'Upload', ms: 0, status: 'skipped' },
  { key: 'publish', label: 'Publish', ms: 0, status: 'skipped' },
];

/**
 * The log a build that worked leaves behind, generated from its own steps so
 * the two never disagree. Advanced mode shows this under every deploy, and a
 * person who opens it after opening the failed one should recognize the same
 * artifact: same step rule, same clock column, same "Done in" line. A success
 * log written in a different shape from a failure log teaches a person to read
 * the shape instead of the words.
 *
 * The wall clock start is fixed rather than derived from the deploy's own
 * timestamp, because a build log is a record of a run, not a calendar, and
 * nothing on any screen compares this column to anything.
 */
const LOG_START = 9 * 3600 + 14 * 60 + 2;

function clock(seconds: number): string {
  const at = LOG_START + seconds;
  const hh = String(Math.floor(at / 3600) % 24).padStart(2, '0');
  const mm = String(Math.floor(at / 60) % 60).padStart(2, '0');
  const ss = String(at % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

const STEP_LINES: Record<string, string[]> = {
  'deps.install': ['Installing 41 packages'],
  'config.read': ['Found sv.config.json', 'Connections: database, file storage'],
  build: ['Compiling 23 files', 'Compiled 23 files with no errors'],
  upload: ['Uploading 2.1 MB of build output'],
  publish: ['Health check passed in 38ms'],
};

export function builtLog(steps: RecordedStep[]): string {
  const lines: string[] = [];
  let at = 0;

  for (const step of steps) {
    lines.push(`${clock(at)}  ▸ ${step.label}`);
    for (const detail of STEP_LINES[step.key] ?? []) {
      lines.push(`${clock(at)}    ${detail}`);
    }
    const seconds = Math.round(step.ms / 1000);
    at += seconds;
    lines.push(`${clock(at)}    Done in ${seconds}s`);
  }

  lines.push(`${clock(at)}    Live`);
  return lines.join('\n');
}

/** Screen spec 5.5, the machine error, verbatim. */
export const MACHINE_ERROR = `src/routes/invoices.ts(42,10): error TS2551: Property 'formatCurency' does not
exist on type 'typeof import("/src/lib/money")'. Did you mean 'formatCurrency'?`;

/** Screen spec 5.5, the raw build log, verbatim. Do not reflow it. */
export const FAILED_BUILD_LOG = `19:58:02  ▸ Install dependencies
19:58:02    Installing 41 packages
19:58:10    Done in 8.1s
19:58:10  ▸ Read your app configuration
19:58:10    Found sv.config.json
19:58:10    Connections: database, file storage
19:58:10    Done in 0.2s
19:58:11  ▸ Build your app
19:58:11    Compiling 23 files
19:58:24    src/routes/invoices.ts(42,10): error TS2551: Property
19:58:24    'formatCurency' does not exist on type 'typeof
19:58:24    import("/src/lib/money")'. Did you mean 'formatCurrency'?
19:58:24
19:58:24      40 |   const rows = await listInvoices(db, clientId);
19:58:24      41 |   const total = rows.reduce((a, r) => a + r.amount, 0);
19:58:24    > 42 |   return money.formatCurency(total, "THB");
19:58:24         |          ^^^^^^^^^^^^^^^^^^
19:58:24      43 | }
19:58:24
19:58:24    Found 1 error.
19:58:26    Build failed after 15.9s
19:58:26  ▸ Upload            not started
19:58:26  ▸ Publish           not started`;

/**
 * Screen spec 5.6, the copy for agent payload, verbatim.
 *
 * Three deliberate choices in it, and none of them is an accident to tidy up.
 * It opens by naming the product and the situation, so the agent has a frame
 * before it has an error. It carries the code context lines, not just the
 * message, which is the difference between a one shot fix and a round trip.
 * And it closes by telling the agent not to deploy, because the agent will
 * otherwise try to run a deploy command, fail, and confuse the person. That
 * last sentence is the most valuable line in the payload, and it is the kind
 * of thing only the platform knows to say.
 *
 * It is a payload, not a product string, so it does not go through t(). A
 * coding agent reads English, and translating the instruction while the code
 * context stayed English would make the paste worse, not better.
 */
export const FIX_REQUEST = `My app is hosted on SV Cloud. A deploy just failed and I need you to fix it.

Project: Studio Ledger
Deploy: 7c04e1b "Fix the currency formatting typo"
Build step that failed: Build your app

The build error:

  src/routes/invoices.ts(42,10): error TS2551: Property 'formatCurency' does
  not exist on type 'typeof import("/src/lib/money")'. Did you mean
  'formatCurrency'?

    40 |   const rows = await listInvoices(db, clientId);
    41 |   const total = rows.reduce((a, r) => a + r.amount, 0);
  > 42 |   return money.formatCurency(total, "THB");
       |          ^^^^^^^^^^^^^^^^^^
    43 | }

Please find the cause, fix it in my code, and commit the fix to main. Pushing
to main deploys automatically, so do not run any deploy commands.`;

/**
 * Screen spec 5.5, the plain language summary and the plain cause.
 *
 * The cause ends by saying nothing changed on the live site, which is the same
 * fact the failure screen's first zone opens with. Saying it twice is
 * deliberate: the first time is reassurance a person reads in half a second,
 * and the second time is the sentence that goes on the clipboard.
 */
export const TYPO_DIAGNOSIS: BuildDiagnosis = {
  summary: 'Your code has a spelling mistake in a function name.',
  cause:
    'src/routes/invoices.ts calls a function named formatCurency, but the file that defines it spells it formatCurrency. The build stopped before it could finish, so nothing changed on your live site.',
  location: 'src/routes/invoices.ts line 42',
  machineError: MACHINE_ERROR,
  fixRequest: FIX_REQUEST,
  failedStepKey: 'build',
};
